/**
 * Server-backed Personal-Analyst chat history.
 *
 * Thin wrappers over AuthContext's `fetchWithAuth` (401 -> refresh -> retry) for
 * the /api/conversations endpoints. Chat threads live on the server now (they
 * previously lived only in sessionStorage and were wiped on refresh); every call
 * is scoped server-side to the authenticated user and fails closed.
 */
import { API_ENDPOINTS } from "../config/api";

async function asJson(promise) {
  const res = await promise;
  if (!res.ok) throw new Error(`conversations ${res.status}`);
  return res.json();
}

const jsonBody = (method, body) => ({
  method,
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

/** GET the user's conversations (newest first, each with a last-message snippet). */
export function listConversations(fetchWithAuth) {
  return asJson(fetchWithAuth(API_ENDPOINTS.conversations.list));
}

/** POST a new (empty) conversation; the first persisted turn auto-titles it. */
export function createConversation(fetchWithAuth, title = null) {
  return asJson(
    fetchWithAuth(API_ENDPOINTS.conversations.create, jsonBody("POST", { title: title ?? null }))
  );
}

/** GET one conversation's metadata + messages (ordered). */
export function getConversation(fetchWithAuth, id) {
  return asJson(fetchWithAuth(API_ENDPOINTS.conversations.detail(id)));
}

/** PATCH a conversation's title. */
export function renameConversation(fetchWithAuth, id, title) {
  return asJson(
    fetchWithAuth(API_ENDPOINTS.conversations.rename(id), jsonBody("PATCH", { title }))
  );
}

/** DELETE (soft) a conversation. */
export function deleteConversation(fetchWithAuth, id) {
  return asJson(fetchWithAuth(API_ENDPOINTS.conversations.remove(id), { method: "DELETE" }));
}

/** DELETE (soft) ALL of the caller's conversations. */
export function deleteAllConversations(fetchWithAuth) {
  return asJson(fetchWithAuth(API_ENDPOINTS.conversations.removeAll, { method: "DELETE" }));
}

/** POST a thumbs vote for a message: 1 up, -1 down, 0 clear. */
export function sendFeedback(fetchWithAuth, messageId, value) {
  return asJson(
    fetchWithAuth(API_ENDPOINTS.conversations.feedback(messageId), jsonBody("POST", { value }))
  );
}
